import React, { Component } from 'react';
import lambdaClient from '../../lambdaClient';
import firebase, {
    db,
    SYSTEM_LOGIN,
    COLLECTION_BOOKINGS,
    COLLECTION_BOOKING_DATES_REF,
} from '../../firebase';
import mailer from '../../services/mailer';
import logger from '../../services/logger';
import {
    convertTimestampToMoment,
    createBookingData,
    createDateTimeBookingData
} from '../../utils';
import API_KEYS from '../../constants/apiKeys';


const { API_GATEWAY_URL } = API_KEYS;

export function withFormHandler(WrappedComponent) {
    return class extends Component {
        state = {
            activeStep: 0,
            isLoading: false,
            isComplete: false,
            hasError: false,
            token: null,
            postData: null,
            friendlyTimeslot: '',
        };

        submitStep = values => {
            window.scrollTo(0, 0);

            this.setState(prevState => ({
                activeStep: prevState.activeStep + 1,
                formValues: { ...values, ...prevState.formValues },
            }));
        }

        submitForm = (values, token) => {
            const { formValues } = this.state;
            const momentObj = convertTimestampToMoment(formValues.bookingDateTime);

            this.setState({
                token,
                isLoading: true,
                formValues: { ...formValues, ...values },
                friendlyTimeslot: momentObj.format('LLLL'),
            }, this.validateBooking);
        }

        validateBooking = async () => {
            const { formValues, friendlyTimeslot } = this.state;

            const snapshot = await db.collection(COLLECTION_BOOKING_DATES_REF)
                .where('date_time', '==', formValues.bookingDateTime)
                .get();

            if (!snapshot.empty) {
                return this.handleError(`Error, booking timeslot already taken: ${friendlyTimeslot}`);
            }

            this.signInAsAdmin();
        }

        signInAsAdmin = async () => {
            try {
                await firebase.auth().signInWithEmailAndPassword(SYSTEM_LOGIN.USER, SYSTEM_LOGIN.PASSWORD);

                this.createCharge();
            } catch (e) {
                this.handleError('Error trying to sign in before booking: ' + JSON.stringify(e));
            }
        }

        createCharge = () => {
            const { formValues, token } = this.state;

            const postData = createBookingData(formValues);

            this.setState({
                postData: createBookingData(formValues),
            }, () => {
                const url = `${API_GATEWAY_URL}/stripe-create-charge`;
                const payload = { token, postData };

                const handleError = async (e) => {
                    const url = `${API_GATEWAY_URL}/mail/create-booking`;
                    const payload = mailer.createPaymentFailureNotification(postData);

                    const handleSuccess = async () => {
                        logger.notify(`Payment failure notification email sent: ${postData.uuid}`);
                    };

                    lambdaClient(url, 'POST', payload, handleSuccess, () => logger.error('Could not send payment failure notifiation email (customer may have been charged)'));

                    this.handleError('Error trying to create a charge: ', JSON.stringify(e));
                };

                lambdaClient(url, 'POST', payload, this.createCustomer, handleError);
            });
        }

        createCustomer = async () => {
            const { postData } = this.state;
            const bookingsRef = db.collection(COLLECTION_BOOKINGS);
            const bookingDateTimeRef = db.collection(COLLECTION_BOOKING_DATES_REF);

            try {
                await bookingsRef.doc(postData.uuid).set(postData);
                await bookingDateTimeRef.doc(postData.uuid).set(createDateTimeBookingData(postData));

                this.setState({ isLoading: false, isComplete: true });

                this.sendCreateBookingEmail(postData);
            } catch (e) {
                const error = `Error trying to create a customer in Firebase (customer already charged: ${postData.email_address}): ` + JSON.stringify(e);

                this.handleError(error);
            }
        }

        sendCreateBookingEmail = async () => {
            const { postData, friendlyTimeslot } = this.state;
            const url = `${API_GATEWAY_URL}/mail/create-booking`;

            const payload = mailer.createConfigNewBooking(postData);

            const handleSuccess = async () => {
                logger.notify(`New booking created: ${postData.uuid} - ${friendlyTimeslot}`);

                await firebase.auth().signOut();
            };

            const handleError = async (e) => {
                const { postData } = this.state;
                const bookingsRef = db.collection(COLLECTION_BOOKINGS).doc(postData.uuid);

                await bookingsRef.update({ email_booking_error: true });
                await firebase.auth().signOut();

                logger.error(`Error sending sending email (post data: ${postData}). Message: ` + JSON.stringify(e));
            };

            lambdaClient(url, 'POST', payload, handleSuccess, handleError);
        }

        handleError = (error = null) => {
            if (error) {
                logger.error(error);
            }

            this.setState({ isLoading: false, hasError: true });
        }

        render() {
            const {
                activeStep,
                isLoading,
                isComplete,
                hasError,
                formValues,
            } = this.state;

            return (
                <WrappedComponent
                    submitForm={this.submitForm}
                    submitStep={this.submitStep}
                    activeStep={activeStep}
                    isLoading={isLoading}
                    isComplete={isComplete}
                    hasError={hasError}
                    formValues={formValues}
                    {...this.props}
                />
            );
        }
    }
}
